<script setup lang="ts">
import { ref, watch, nextTick, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useEditions } from '@/composables/useEditions'
import { useAuth } from '@/composables/useAuth'
import { useUnsavedChanges } from '@/composables/useUnsavedChanges'
import EditionForm from '@/components/organizer/events/EditionForm.vue'
import type { StoreEditionPayload, UpdateEditionPayload } from '@/types/events'

interface EditionFormData {
  name: string
  start_date: string
  end_date: string
  venue_id: number | null
  active: boolean
}

function isAxiosError(e: unknown): e is { response: { status: number; data: { errors?: Record<string, string[]>; message?: string } } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: unknown }).response !== null
  )
}

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const { currentOrganisation } = useAuth()
const { edition, fetchEdition, updateEdition } = useEditions()
const { markDirty, markClean } = useUnsavedChanges()

const formLoading = ref(false)
const errors = ref<Record<string, string[]>>({})
const generalError = ref('')
const successMessage = ref('')
const initialized = ref(false)

const eventSlug = route.params['eventSlug'] as string
const editionSlug = route.params['editionSlug'] as string

const form = ref<EditionFormData>({
  name: '',
  start_date: '',
  end_date: '',
  venue_id: null,
  active: false,
})

watch(form, () => {
  if (initialized.value) markDirty(route.name as string)
}, { deep: true })

onMounted(async () => {
  // Refetch when the cached edition belongs to another edition, or when it
  // carries no `venue` key at all - which is exactly what an update response
  // looks like. The show endpoint always eager-loads the venue, so after this
  // guard `edition.venue` is present (possibly null for a legacy edition).
  // Without it the picker below would silently prefill as "no venue" and the
  // submit button would stay disabled with no explanation.
  if (
    !edition.value ||
    edition.value.slug !== editionSlug ||
    edition.value.venue === undefined
  ) {
    await fetchEdition(eventSlug, editionSlug)
  }
  if (edition.value) {
    form.value = {
      name: edition.value.name,
      start_date: edition.value.start_date,
      end_date: edition.value.end_date,
      venue_id: edition.value.venue?.id ?? null,
      active: edition.value.active,
    }
  }
  await nextTick()
  initialized.value = true
})

async function handleSubmit(data: StoreEditionPayload | UpdateEditionPayload): Promise<void> {
  errors.value = {}
  generalError.value = ''
  successMessage.value = ''
  formLoading.value = true
  try {
    await updateEdition(eventSlug, editionSlug, data as UpdateEditionPayload)
    markClean(route.name as string)
    successMessage.value = t('editions.settings.save_success')
  } catch (e: unknown) {
    if (isAxiosError(e)) {
      if (e.response.status === 422 && e.response.data.errors) {
        errors.value = e.response.data.errors
      } else {
        generalError.value = t('editions.form.error_generic')
      }
    } else {
      generalError.value = t('editions.form.error_generic')
    }
  } finally {
    formLoading.value = false
  }
}
</script>

<template>
  <div>
    <div
      v-if="successMessage"
      class="mb-6 rounded-xl bg-emerald-500/10 border border-emerald-500/30 px-5 py-3 text-sm text-emerald-400"
    >
      {{ successMessage }}
    </div>

    <div
      v-if="generalError"
      class="mb-6 rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400"
    >
      {{ generalError }}
    </div>

    <div class="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 sm:p-8">
      <EditionForm
        v-model="form"
        :loading="formLoading"
        :errors="errors"
        :organisation-id="currentOrganisation?.id ?? 0"
        :is-edit="true"
        @submit="handleSubmit"
        @cancel="router.push(`/organizer/events/${eventSlug}/editions/${editionSlug}`)"
      />
    </div>
  </div>
</template>
